import React, { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';

/**
 * Matches private-file references produced by the backend's fileStorage
 * (`/api/files/{fileId}`). These are not directly fetchable URLs — the client
 * must exchange them for a short-lived signed URL via GET /agent-files/:fileId.
 */
const FILE_REF_REGEX = /^\/api\/files\/(file-[\w-]+)$/;

export function isFileReferenceUrl(url: string): boolean {
  return FILE_REF_REGEX.test(url);
}

interface FileLinkProps {
  url: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

/**
 * Renders a link that transparently handles private file references.
 *
 * - Regular URLs render as a plain `<a target="_blank">`.
 * - `/api/files/{fileId}` references resolve a signed download URL (with auth)
 *   on click and open it in a new tab.
 */
const FileLink: React.FC<FileLinkProps> = ({
  url,
  className,
  style,
  onClick,
  children,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const match = url.match(FILE_REF_REGEX);
  const fileId = match ? match[1] : null;

  const handleFileClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.(e);
      if (!fileId) return;

      // Open the tab synchronously so popup blockers don't kill it; we point
      // it at the signed URL once it arrives (it only lives ~30s anyway).
      const newTab = window.open('', '_blank');
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{
          success: boolean;
          downloadUrl: string;
        }>(`/agent-files/${fileId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success && res.data.downloadUrl) {
          if (newTab) {
            newTab.location.href = res.data.downloadUrl;
          } else {
            window.location.href = res.data.downloadUrl;
          }
        } else {
          newTab?.close();
          console.error('FileLink: no download URL returned for', fileId);
        }
      } catch (err) {
        newTab?.close();
        console.error('FileLink: failed to resolve download URL', err);
      }
    },
    [fileId, getAccessTokenSilently, onClick],
  );

  if (!fileId) {
    return (
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        className={className}
        style={style}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <a href={url} className={className} style={style} onClick={handleFileClick}>
      {children}
    </a>
  );
};

export default FileLink;
